/* eslint-disable react-refresh/only-export-components */
/**
 * TimeWindowSelector
 *
 * US-17-GR-05 — Time-Window Filtering for Admin Metrics
 *
 * Renders a pill-button group allowing the user to select 7d / 30d / 90d.
 * Persists the selection to localStorage key `admin.timeWindow.days`.
 *
 * Copied verbatim from user app components/agent/admin/TimeWindowSelector.tsx.
 * The admin site is a separate app — copy, not import.
 */

const STORAGE_KEY = 'admin.timeWindow.days';
const VALID_WINDOWS = [7, 30, 90] as const;

export type TimeWindow = 7 | 30 | 90;

/** Read the persisted window from localStorage, sanitizing to only allow valid values. */
export function loadStoredWindow(): TimeWindow {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw !== null) {
      const parsed = parseInt(raw, 10);
      if ((VALID_WINDOWS as readonly number[]).includes(parsed)) {
        return parsed as TimeWindow;
      }
    }
  } catch {
    // localStorage unavailable (SSR / private browsing quota)
  }
  return 30;
}

/** Persist the selected window to localStorage. */
export function saveStoredWindow(window: TimeWindow): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(window));
  } catch {
    // Storage quota or access denied — silently ignore
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TimeWindowSelectorProps {
  value: TimeWindow;
  onChange: (window: TimeWindow) => void;
}

const BUTTONS: { label: string; value: TimeWindow }[] = [
  { label: '7d', value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

export function TimeWindowSelector({ value, onChange }: TimeWindowSelectorProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Time window selector"
      className="flex gap-1"
    >
      {BUTTONS.map((btn) => {
        const isSelected = value === btn.value;
        return (
          <button
            key={btn.value}
            type="button"
            role="radio"
            aria-checked={isSelected ? 'true' : 'false'}
            aria-label={`${btn.value} days`}
            onClick={() => onChange(btn.value)}
            className={[
              'px-3 py-1 rounded-full text-xs font-medium transition-colors min-h-[48px]',
              isSelected
                ? 'bg-violet-600 text-zinc-950 shadow-sm'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200',
            ].join(' ')}
          >
            {btn.label}
          </button>
        );
      })}
    </div>
  );
}
